import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  HomeOutlined,
  MonitorHeartOutlined,
  BarChartOutlined,
  AssessmentOutlined,
  StorageOutlined,
  SyncAltOutlined,
  HelpOutlineOutlined,
  OpenInNewOutlined,
  // HealthAndSafetyOutlined
} from '@mui/icons-material';
import { GUIDE_URL, HELPDESK_URL, openExternal } from '../../../utils/externalLinks';
import { useTranslation } from 'react-i18next';
import { isKorean } from '../../../utils/lang';
import './Sidebar.scss';

const WIDTH_KEY = 'grap-sidebar-width';
const MIN_WIDTH = 200;
const MAX_WIDTH = 420;
const COLLAPSED_WIDTH = 64;

// 영어 라벨이 한국어보다 길어서 기본 240px 에서는 "GWRPT - Coming soon" 같은
// 항목이 잘립니다. 시작 폭만 언어에 맞춰 넓히고, 그 뒤로는 사용자가 조절한
// 값을 씁니다.
const defaultWidth = () => (isKorean() ? 240 : 280);

const clampWidth = (value) => Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, value));

const readStoredWidth = () => {
  try {
    const stored = parseInt(window.localStorage.getItem(WIDTH_KEY), 10);
    if (Number.isFinite(stored)) return clampWidth(stored);
  } catch (_) {
    // 저장소가 막힌 환경에서는 기본값으로 시작합니다.
  }
  return defaultWidth();
};

const Sidebar = ({ isExpanded, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [width, setWidth] = useState(readStoredWidth);
  const [isResizing, setIsResizing] = useState(false);
  const currentWidth = isExpanded ? width : COLLAPSED_WIDTH;

  // 본문 오프셋은 예전에 240/64 를 직접 박아 두었는데, 폭이 가변이 되면서
  // 어긋나게 됩니다. CSS 변수 한 곳으로 모아 사이드바와 본문이 같은 값을
  // 보도록 했습니다. LayoutContainer.scss 도 이 변수를 씁니다.
  //
  // useEffect 면 첫 페인트 뒤에 실행됩니다. 사이드바는 이미 저장된 폭으로
  // 그려졌는데 본문은 아직 폴백 240px 이라, 저장값이 240 이 아니면 페이지를
  // 열 때마다 본문이 0.3초 동안 미끄러집니다. useLayoutEffect 는 페인트
  // 직전에 실행되므로 그 움직임이 보이지 않습니다.
  useLayoutEffect(() => {
    document.documentElement.style.setProperty(
      '--sidebar-current-width',
      `${currentWidth}px`
    );

    const mainContent = document.querySelector('.main-content');
    if (mainContent) {
      mainContent.style.marginLeft = `${currentWidth}px`;
      mainContent.style.width = `calc(100% - ${currentWidth}px)`;
    }
  }, [currentWidth]);

  // 드래그 중에는 문서 전체에서 마우스를 따라가야 합니다. 포인터가 사이드바
  // 밖으로 나가도 놓칠 수 없기 때문입니다.
  useEffect(() => {
    if (!isResizing) return undefined;

    const handleMove = (e) => {
      // 창 밖에서 버튼을 놓으면 mouseup 이 오지 않아 드래그가 끝나지 않습니다.
      // 포인터가 돌아왔을 때 버튼이 눌려 있지 않으면 그때 정리합니다.
      if (e.buttons === 0) {
        setIsResizing(false);
        return;
      }
      setWidth(clampWidth(e.clientX));
    };
    const handleUp = () => setIsResizing(false);

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    // 드래그 도중 글자가 선택되면 사이드바가 파랗게 물듭니다.
    document.body.classList.add('sidebar-resizing');

    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      document.body.classList.remove('sidebar-resizing');
    };
  }, [isResizing]);

  // 드래그가 끝난 뒤에만 저장합니다. 이동 중 매 프레임 쓰면 낭비입니다.
  //
  // 첫 렌더에도 한 번 실행되므로 언어별 기본값(240/280)이 바로 저장됩니다.
  // 그래서 나중에 브라우저 언어를 바꿔도 폭은 따라오지 않습니다. 손잡이에
  // 포커스를 두고 Home 을 누르면 현재 언어 기준으로 되돌아갑니다.
  useEffect(() => {
    if (isResizing) return;
    try {
      window.localStorage.setItem(WIDTH_KEY, String(width));
    } catch (_) {
      // 저장에 실패해도 이번 세션 동안의 폭은 유지됩니다.
    }
  }, [width, isResizing]);

  // 키보드로도 조절할 수 있게 합니다. 마우스로만 되면 접근성이 떨어집니다.
  const handleResizerKeyDown = useCallback((e) => {
    const step = e.shiftKey ? 32 : 8;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setWidth((w) => clampWidth(w - step));
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      setWidth((w) => clampWidth(w + step));
    } else if (e.key === 'Home') {
      e.preventDefault();
      setWidth(defaultWidth());
    }
  }, []);

  const toggleSidebar = () => {
    onToggle(!isExpanded);
  };

  // 메뉴 이름은 브라우저 언어를 따릅니다. 다만 제품 이름은 고유명사라 두 언어에서
  // 그대로 쓰며, 번역 키를 거치지 않는 것은 이 일곱뿐입니다. GWSOS, GWSAR,
  // WorkBook, CmdExcel, MariaDB, MySQL, PostgreSQL.
  //
  // GWRPT/GWMHA/GWDBH 는 제품 코드는 그대로 두고 "준비중" 부분만 바뀝니다.
  const menuItems = [
    {
      title: t('nav.about'),
      icon: <HomeOutlined />,
      path: '/home'
    },
    {
      title: 'GWSOS',
      icon: <MonitorHeartOutlined />,
      path: '/sosguide',
      submenu: [
        {
          title: t('nav.history'),
          path: '/history'
        }
        // ,{
        //   title: 'Detail',
        //   path: '/detail'
        // }
      ]
    },
    {
      title: 'GWSAR',
      icon: <BarChartOutlined />,
      path: '/sarguide',
      submenu: [
        {
          title: t('nav.generator'),
          path: '/sarimport'
        }
      ]
    },
    {
      title: 'WorkBook',
      icon: <BarChartOutlined />,
      path: '/workbook',
      submenu: [
        {
          title: 'CmdExcel',
          path: '/cmdexcel'
        }
      ]
    },
    // 아래 세 메뉴는 준비중이라 하위 항목을 노출하지 않습니다. comingSoon 플래그만
    // 지우면 submenu 가 그대로 다시 보이도록, 목록 자체는 남겨 두었습니다.
    // 경로는 Navigator 에 그대로 등록되어 있어 직접 접근은 계속 가능합니다.
    // 하위 항목 이름도 지금 함께 번역해 두었으므로 플래그만 지우면 됩니다.
    {
      title: `GWRPT - ${t('nav.comingSoon')}`,
      icon: <AssessmentOutlined />,
      path: '/rptguide',
      comingSoon: true,
      submenu: [
        {
          title: t('nav.report'),
          path: '/gwreport'
        }
      ]
    },
    {
      title: `GWMHA - ${t('nav.comingSoon')}`,
      icon: <SyncAltOutlined />,
      path: '/mhaguide',
      comingSoon: true,
      submenu: [
        {
          title: t('nav.status'),
          path: '/mhastatus'
        }
      ]
    },
    {
      title: `GWDBH - ${t('nav.comingSoon')}`,
      icon: <StorageOutlined />,
      path: '/dbhguide',
      comingSoon: true,
      submenu: [
        {
          title: 'MariaDB',
          path: '/mariadb'
        },
        {
          title: 'MySQL',
          path: '/mysql'
        },
        {
          title: 'PostgreSQL',
          path: '/postgresql'
        }
      ]
    }
  ];

  // 목록과 분리해 사이드바 맨 아래에 고정되는 메뉴입니다. 위 목록이 길어져
  // 스크롤이 생겨도 항상 같은 자리에 남습니다.
  const footerMenuItems = [
    {
      title: t('nav.help'),
      icon: <HelpOutlineOutlined />,
      // 부모는 진입 페이지 없이 그룹 역할만. 클릭 시 navigate 가드로 무시.
      submenu: [
        {
          title: t('nav.guide'),
          icon: <OpenInNewOutlined />,
          external: true,
          url: GUIDE_URL
        },
        {
          title: t('nav.helpdesk'),
          icon: <OpenInNewOutlined />,
          external: true,
          url: HELPDESK_URL
        }
      ]
    }
  ];

  // 메뉴 클릭 핸들러 — 외부 링크는 새 탭으로, 내부 경로는 navigate, path 없으면 무시.
  const handleMenuClick = (item) => {
    if (item.external && item.url) {
      openExternal(item.url);
      return;
    }
    if (item.path) {
      navigate(item.path);
    }
  };

  // 하위 항목 하나를 그립니다. 접힘 여부와 무관하게 모양이 같아 두 곳에서
  // 함께 씁니다.
  const renderSubItem = (subItem, subIndex) => (
    <div
      key={subIndex}
      className={`submenu-item ${subItem.className || ''} ${subItem.path && location.pathname === subItem.path ? 'active' : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        if (!subItem.disabled) {
          handleMenuClick(subItem);
        }
      }}
    >
      <div className="menu-icon">{subItem.icon}</div>
      <span className="menu-title">{subItem.title}</span>
    </div>
  );

  // 상단 목록과 하단 고정 영역이 똑같은 구조를 쓰므로 한 곳에서 그립니다.
  const renderMenuItem = (item, index) => (
    <div key={index} className="menu-item-wrapper">
      <div
        className={`menu-item ${item.path && location.pathname === item.path ? 'active' : ''}`}
        onClick={() => handleMenuClick(item)}
        // 접힌 상태에서는 아이콘만 남습니다. 하위 메뉴가 있으면 떠 있는
        // 패널이 이름을 보여주지만, 준비중 메뉴나 About 처럼 패널이 없는
        // 항목은 이름을 알 방법이 없어 기본 툴팁으로 보완합니다. 패널이
        // 뜨는 항목까지 툴팁을 달면 커서 옆 툴팁이 패널 왼쪽을 가립니다.
        title={
          !isExpanded && (!item.submenu || item.comingSoon)
            ? item.title
            : undefined
        }
      >
        <div className="menu-icon">{item.icon}</div>
        <span className="menu-title">{item.title}</span>
      </div>

      {item.submenu && !item.comingSoon && !isExpanded && (
        <div className="submenu floating">
          <div
            className={`floating-menu-title ${item.path && location.pathname === item.path ? 'active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              handleMenuClick(item);
            }}
          >
            {item.title}
          </div>
          {item.submenu.map(renderSubItem)}
        </div>
      )}

      {item.submenu && !item.comingSoon && isExpanded && (
        <div className="submenu visible">{item.submenu.map(renderSubItem)}</div>
      )}
    </div>
  );

  return (
    <div
      className={`sidebar ${isExpanded ? 'expanded' : 'collapsed'} ${
        isResizing ? 'resizing' : ''
      }`}
      style={{ width: `${currentWidth}px` }}
    >
      <header className="sidebar-header">
        <div className="header-content">
          <span className="dashboard-title">
            {t('nav.dashboard')}
          </span>
          <button className="toggler sidebar-toggler" onClick={toggleSidebar}>
            <ChevronLeft className={isExpanded ? '' : 'rotate-180'} />
          </button>
        </div>
      </header>

      <div className="menu-items">{menuItems.map(renderMenuItem)}</div>

      {/* 스크롤 영역 바깥이라 목록이 길어져도 항상 맨 아래에 보입니다. */}
      <div className="menu-footer">{footerMenuItems.map(renderMenuItem)}</div>

      {/* 접힌 상태는 아이콘 폭이 고정이라 조절 손잡이를 두지 않습니다. */}
      {isExpanded && (
        <div
          className="sidebar-resizer"
          onMouseDown={(e) => {
            // 가운데/오른쪽 버튼으로는 시작하지 않습니다.
            if (e.button !== 0) return;
            e.preventDefault();
            setIsResizing(true);
          }}
          onKeyDown={handleResizerKeyDown}
          role="separator"
          aria-orientation="vertical"
          aria-label={t('nav.resizeSidebar')}
          aria-valuenow={width}
          aria-valuemin={MIN_WIDTH}
          aria-valuemax={MAX_WIDTH}
          tabIndex={0}
          title={t('nav.resizeSidebar')}
        />
      )}
    </div>
  );
};

export default Sidebar;